import { z } from 'zod';

/**
 * IMS role vocabulary (RBAC Phase R.3a, #315).
 *
 * Mirrors the operator-action verb-FK anti-clobber pattern (Task #90 / migration
 * 0045): the canonical list is THIS const-tuple; the DB enforces it via an FK from
 * `users.role` into the `valid_user_roles` lookup table (migration 0083) — NOT a
 * CHECK enum, so parallel branches can extend the vocabulary with ONE idempotent
 * INSERT row instead of colliding on a constraint rewrite. A new role = one literal
 * here + one seed INSERT in a migration + schema.ts lockstep, all pinned together
 * by `ims-roles.test.ts`.
 *
 * v1 PERMISSION MODEL (deliberately minimal — a CTO-proposed default the founder
 * may later refine into a finer matrix):
 *   - admin                          → everything, including user / role management.
 *   - operational (eoc-director,
 *     situation-unit, logistics,
 *     liaison)                       → all COP operator actions. They share ONE
 *                                      permission set in v1; the distinct role is
 *                                      recorded for audit, for future per-role
 *                                      granularity, and as a layer-preference seam.
 *   - viewer                         → READ-ONLY. Operator-action (mutation) routes
 *                                      return 403 when AUTH_ENFORCED is on.
 *
 * Enforcement rides AUTH_ENFORCED (ships OFF ⇒ non-breaking; the demo/dev identity
 * assumes `admin` so an un-authenticated COP stays fully usable). NO tenancy /
 * agency row-scoping lives here — that is R.3b.
 */
export const IMS_ROLES = [
  'admin',
  'eoc-director',
  'situation-unit',
  'logistics',
  'liaison',
  'viewer',
] as const;

export const ImsRoleSchema = z.enum(IMS_ROLES);
export type ImsRole = z.infer<typeof ImsRoleSchema>;

/** The operational (non-admin, non-viewer) roles — full COP operator-action access
 *  in v1, recorded distinctly for audit + future granularity. */
export const OPERATIONAL_IMS_ROLES = [
  'eoc-director',
  'situation-unit',
  'logistics',
  'liaison',
] as const satisfies readonly ImsRole[];

/** The role a brand-new account defaults to (matches the migration 0083 column
 *  DEFAULT and the create-user non-first-user default). */
export const DEFAULT_IMS_ROLE: ImsRole = 'situation-unit';

/** The permissive role the demo / dev identity assumes when AUTH_ENFORCED is OFF,
 *  so the un-authenticated COP stays fully unrestricted. */
export const DEMO_IMS_ROLE: ImsRole = 'admin';

/**
 * The single v1 permission predicate: may this role perform COP operator actions
 * (the mutation / write surface)? Everyone except `viewer` can; `viewer` is
 * read-only. Admin and the operational roles are identical here in v1 (see the
 * matrix above) — the distinction exists for audit + future granularity.
 */
export function roleCanPerformOperatorActions(role: ImsRole): boolean {
  return role !== 'viewer';
}

/** Admin-only capability gate (user / role management). Reserved for v1 — the
 *  create-user CLI is inherently admin-run, and any future role-assignment API
 *  gates on this. */
export function roleIsAdmin(role: ImsRole): boolean {
  return role === 'admin';
}

/**
 * Coerce an arbitrary stored / wire value into an `ImsRole`, defaulting to the
 * LEAST-privileged role (`viewer`) on any unexpected value — defense in depth so a
 * malformed DB row or a stale server response can never silently grant write
 * access. (The `users.role` FK already guarantees a valid value at rest; this
 * guards the read paths regardless.)
 */
export function parseRoleOrViewer(value: unknown): ImsRole {
  const parsed = ImsRoleSchema.safeParse(value);
  return parsed.success ? parsed.data : 'viewer';
}
